import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, AlertCircle } from "lucide-react";
import { Eyebrow, EASE } from "@/components/motion";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const planName = localStorage.getItem("pending_checkout_plan");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          localStorage.removeItem("pending_checkout_plan");
          return;
        }
        if (data.status === "failed" || data.status === "expired") { setState("error"); return; }
        tries.current += 1;
        if (tries.current < 20) setTimeout(poll, 2000);
        else setState("pending");
      } catch {
        setState("error");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-20 px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="w-full max-w-lg bg-[#101218] border border-white/10 rounded-2xl p-10 md:p-14 text-center shadow-[0_30px_80px_rgba(0,0,0,0.5)] relative overflow-hidden"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
        {state === "checking" && (
          <>
            <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-serif text-2xl text-white mb-3">Confirming your payment…</h1>
            <p className="text-slate-400 text-sm">This takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <div className="w-16 h-16 rounded-full bg-emerald/10 border border-emerald/30 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald" />
            </div>
            <Eyebrow className="mb-3">Payment Confirmed</Eyebrow>
            <h1 className="font-serif text-3xl text-white mb-4">Welcome aboard.</h1>
            <p className="text-slate-400 text-sm leading-relaxed mb-8">
              {planName ? `Your ${planName} plan is active. ` : "Your payment is confirmed. "}
              A senior credit architect will reach out within one business day to kick off your file review.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" data-testid="success-book-call"
                className="bg-gold text-obsidian hover:bg-gold-light font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 rounded-xl transition-all shadow-[0_0_20px_rgba(93,123,255,0.35)]">
                Book Your Kickoff Call
              </Link>
              <Link to="/" data-testid="success-home"
                className="bg-white/5 border border-white/15 text-white hover:bg-white/10 font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 rounded-xl transition-all">
                Back Home
              </Link>
            </div>
          </div>
        )}
        {state === "pending" && (
          <>
            <Clock className="w-10 h-10 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-2xl text-white mb-3">Payment processing</h1>
            <p className="text-slate-400 text-sm leading-relaxed">
              Your payment is being processed. You'll receive an email as soon as it confirms.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-6" />
            <h1 className="font-serif text-2xl text-white mb-3">We couldn't confirm that payment</h1>
            <p className="text-slate-400 text-sm leading-relaxed mb-8">
              If your card was charged, don't worry — call us at (727) 855-7612 and we'll sort it out immediately.
            </p>
            <Link to="/pricing" data-testid="error-back-pricing"
              className="inline-block bg-white/5 border border-white/15 text-white hover:bg-white/10 font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 rounded-xl transition-all">
              Back to Pricing
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
